import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { api, STATUS_LABEL, formatApiErrorDetail, formatMoney, DOCUMENT_TYPES, EPS_LIST, PAYMENT_METHODS } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, UserPlus, Info, Lock, Ban } from "lucide-react";
import FinalizeAppointmentDialog from "@/components/FinalizeAppointmentDialog";
import AnnulAppointmentDialog from "@/components/AnnulAppointmentDialog";
import { useAuth } from "@/context/AuthContext";

const STATUSES = ["pendiente", "confirmada", "en_atencion", "finalizada", "cancelada"];

function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function fromLocalInput(v) {
  if (!v) return new Date().toISOString();
  return new Date(v).toISOString();
}
function isSameDayLocal(a, b) {
  if (!a || !b) return false;
  // Comparar en zona horaria de la clínica (America/Bogota) para coincidir
  // con la lógica del backend, evitando desfases si el navegador está en otra TZ.
  const TZ = "America/Bogota";
  const fmt = (d) => new Date(d).toLocaleDateString("en-CA", { timeZone: TZ });
  return fmt(a) === fmt(b);
}

export default function AppointmentSheet({ open, onClose, appointment, doctors, treatments, cashOpen = true, onSaved, onStatusChange }) {
  const isNew = !appointment?.id;
  const { user } = useAuth();
  const [annulOpen, setAnnulOpen] = useState(false);
  const [patientQuery, setPatientQuery] = useState("");
  const [patientOpts, setPatientOpts] = useState([]);
  const [createMode, setCreateMode] = useState(false);
  const [newPatient, setNewPatient] = useState({ name: "", phone: "", email: "", document_type: "CC", document_number: "", eps: "" });
  const [creating, setCreating] = useState(false);
  const [payDialog, setPayDialog] = useState({ open: false, method: "efectivo", amount: "" });
  const [finalizeOpen, setFinalizeOpen] = useState(false);
  const [form, setForm] = useState({
    patient_id: "", patient_name: "",
    doctor_id: "", treatment_id: "", treatment_name: "",
    start: "", end: "", status: "pendiente", price: 0, notes: "",
  });

  useEffect(() => {
    if (!open) return;
    if (isNew) {
      const start = new Date(); start.setMinutes(0, 0, 0); start.setHours(start.getHours() + 1);
      const end = new Date(start); end.setMinutes(end.getMinutes() + 30);
      setForm({
        patient_id: "", patient_name: "",
        doctor_id: doctors[0]?.id || "",
        treatment_id: "", treatment_name: "",
        start: toLocalInput(start.toISOString()),
        end: toLocalInput(end.toISOString()),
        status: "pendiente", price: 0, notes: "",
      });
      setPatientQuery("");
      setCreateMode(false);
      setNewPatient({ name: "", phone: "", email: "", document_type: "CC", document_number: "", eps: "" });
    } else {
      setForm({
        patient_id: appointment.patient_id,
        patient_name: appointment.patient?.name || "",
        doctor_id: appointment.doctor_id,
        treatment_id: appointment.treatment_id || "",
        treatment_name: appointment.treatment_name || "",
        start: toLocalInput(appointment.start),
        end: toLocalInput(appointment.end),
        status: appointment.status,
        price: appointment.price || 0,
        notes: appointment.notes || "",
      });
      setPatientQuery(appointment.patient?.name || "");
    }
  }, [open, appointment, isNew, doctors]);

  useEffect(() => {
    let cancel = false;
    if (!patientQuery || patientQuery.length < 2) { setPatientOpts([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/patients", { params: { q: patientQuery } });
        if (!cancel) setPatientOpts(data.slice(0, 6));
      } catch { /* noop */ }
    }, 200);
    return () => { cancel = true; clearTimeout(t); };
  }, [patientQuery]);

  const onTreatmentChange = (id) => {
    const t = treatments.find((x) => x.id === id);
    setForm((f) => ({
      ...f,
      treatment_id: id,
      treatment_name: t?.name || "",
      price: t?.price ?? f.price,
      end: t?.duration_min && f.start ? toLocalInput(new Date(new Date(f.start).getTime() + t.duration_min*60000).toISOString()) : f.end,
    }));
  };

  const startCreate = () => {
    setCreateMode(true);
    setNewPatient({ name: patientQuery || "", phone: "", email: "", document_type: "CC", document_number: "", eps: "" });
    setPatientOpts([]);
  };

  const submitCreatePatient = async () => {
    if (!newPatient.name?.trim()) { toast.error("Ingresa un nombre"); return; }
    setCreating(true);
    try {
      const { data: p } = await api.post("/patients", {
        name: newPatient.name.trim(),
        phone: newPatient.phone?.trim() || "",
        email: newPatient.email?.trim() || "",
        document_type: newPatient.document_type || "CC",
        document_number: newPatient.document_number?.trim() || "",
        eps: newPatient.eps?.trim() || "",
      });
      setForm((f) => ({ ...f, patient_id: p.id, patient_name: p.name }));
      setPatientQuery(p.name);
      setCreateMode(false);
      setNewPatient({ name: "", phone: "", email: "", document_type: "CC", document_number: "", eps: "" });
      toast.success("Paciente creado");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.patient_id) { toast.error("Selecciona un paciente"); return; }
    if (!form.doctor_id) { toast.error("Selecciona un doctor"); return; }
    // Solo bloquear si la cita es para HOY (hora local) y la caja está cerrada.
    // Citas a futuro o pasado se permiten aunque la caja esté cerrada.
    const apptIsToday = isSameDayLocal(form.start, new Date());
    if (apptIsToday && !cashOpen) {
      toast.error("Para agendar citas del día de hoy debes abrir la caja primero.");
      return;
    }
    const payload = {
      patient_id: form.patient_id,
      doctor_id: form.doctor_id,
      treatment_id: form.treatment_id || null,
      treatment_name: form.treatment_name,
      start: fromLocalInput(form.start),
      end: fromLocalInput(form.end),
      status: form.status,
      price: Number(form.price) || 0,
      notes: form.notes,
    };
    try {
      if (isNew) {
        await api.post("/appointments", payload);
        toast.success("Cita creada");
      } else {
        await api.put(`/appointments/${appointment.id}`, payload);
        toast.success("Cita actualizada");
      }
      onSaved && onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const onDelete = async () => {
    if (!appointment?.id) return;
    if (!window.confirm("¿Eliminar esta cita?")) return;
    try {
      await api.delete(`/appointments/${appointment.id}`);
      toast.success("Cita eliminada");
      onSaved && onSaved();
    } catch { toast.error("No se pudo eliminar"); }
  };

  const openFinalizeDialog = () => {
    if (isNew) {
      toast.error("Guarda la cita antes de finalizarla.");
      return;
    }
    setFinalizeOpen(true);
  };

  const isLocked = !isNew && appointment?.status === "finalizada";

  const handleQuickStatus = (s) => {
    if (isLocked) {
      toast.error("La cita ya fue finalizada y su estado no puede modificarse.");
      return;
    }
    if (s === "finalizada" && form.status !== "finalizada") {
      openFinalizeDialog();
    } else {
      setForm((f) => ({ ...f, status: s }));
      onStatusChange && onStatusChange(appointment.id, s);
    }
  };

  const handleDropdownStatus = (newStatus) => {
    if (isLocked) return;
    if (newStatus === "finalizada" && form.status !== "finalizada" && !isNew) {
      openFinalizeDialog();
      return;
    }
    setForm((f) => ({ ...f, status: newStatus }));
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="font-display tracking-tight text-2xl">
            {isNew ? "Nueva cita" : "Editar cita"}
          </SheetTitle>
        </SheetHeader>
        <form onSubmit={submit} className="space-y-4 mt-6" data-testid="appt-form">
          {!cashOpen && form.start && !isSameDayLocal(form.start, new Date()) && (
            <div className="flex items-start gap-2 p-3 rounded-sm bg-muted/60 border border-border text-xs" data-testid="appt-cashbox-future-info">
              <Info className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" />
              <div className="text-muted-foreground">
                La caja del día está cerrada. Puedes guardar esta cita porque es para otro día. Solo necesitarás abrir caja el día de la atención para finalizar/cobrarla.
              </div>
            </div>
          )}
          {!cashOpen && form.start && isSameDayLocal(form.start, new Date()) && (
            <div className="flex items-start gap-2 p-3 rounded-sm bg-destructive/10 border border-destructive/30 text-xs" data-testid="appt-cashbox-today-warn">
              <Info className="w-4 h-4 mt-0.5 text-destructive shrink-0" />
              <div className="text-destructive">
                La cita es para hoy y la caja está cerrada. Debes abrir la caja del día antes de guardar.
              </div>
            </div>
          )}
          <div>
            <label className="label-eyebrow block mb-2">Paciente</label>
            {!createMode ? (
              <>
                <input
                  data-testid="appt-patient"
                  className="input-flat"
                  value={patientQuery}
                  onChange={(e) => { setPatientQuery(e.target.value); setForm(f => ({...f, patient_id: ""})); }}
                  placeholder="Buscar por nombre, teléfono…"
                  autoComplete="off"
                />
                {patientOpts.length > 0 && !form.patient_id && (
                  <div className="mt-1 border border-border rounded-sm bg-card max-h-48 overflow-auto">
                    {patientOpts.map((p) => (
                      <button
                        type="button" key={p.id}
                        onClick={() => { setForm(f => ({...f, patient_id: p.id, patient_name: p.name})); setPatientQuery(p.name); setPatientOpts([]); }}
                        className="w-full text-left p-2 hover:bg-muted text-sm"
                        data-testid={`appt-patient-opt-${p.id}`}
                      >
                        <div className="font-semibold">{p.name}</div>
                        <div className="text-xs text-muted-foreground font-mono">{p.phone}</div>
                      </button>
                    ))}
                  </div>
                )}
                <button
                  type="button"
                  onClick={startCreate}
                  data-testid="appt-create-patient-btn"
                  className="mt-2 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-primary hover:underline"
                >
                  <UserPlus className="w-3.5 h-3.5" />
                  {patientQuery && !form.patient_id
                    ? `Crear paciente "${patientQuery}"`
                    : "Crear nuevo paciente"}
                </button>
              </>
            ) : (
              <div className="border border-border rounded-sm p-3 space-y-3 bg-muted/30" data-testid="appt-create-patient-form">
                <div className="flex items-center justify-between">
                  <div className="label-eyebrow">Nuevo paciente</div>
                  <button
                    type="button"
                    onClick={() => setCreateMode(false)}
                    className="text-xs text-muted-foreground hover:text-foreground"
                  >
                    Cancelar
                  </button>
                </div>
                <input
                  className="input-flat"
                  placeholder="Nombre completo *"
                  value={newPatient.name}
                  onChange={(e) => setNewPatient((n) => ({ ...n, name: e.target.value }))}
                  data-testid="new-patient-inline-name"
                  autoFocus
                />
                <div className="grid grid-cols-3 gap-2">
                  <select
                    className="input-flat"
                    value={newPatient.document_type}
                    onChange={(e) => setNewPatient((n) => ({ ...n, document_type: e.target.value }))}
                    data-testid="new-patient-inline-doctype"
                  >
                    {DOCUMENT_TYPES.map((d) => <option key={d.value} value={d.value}>{d.value}</option>)}
                  </select>
                  <input
                    className="input-flat font-mono col-span-2"
                    placeholder="Número documento"
                    value={newPatient.document_number}
                    onChange={(e) => setNewPatient((n) => ({ ...n, document_number: e.target.value }))}
                    data-testid="new-patient-inline-docnum"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    className="input-flat font-mono"
                    placeholder="Teléfono"
                    value={newPatient.phone}
                    onChange={(e) => setNewPatient((n) => ({ ...n, phone: e.target.value }))}
                    data-testid="new-patient-inline-phone"
                  />
                  <input
                    type="email"
                    className="input-flat"
                    placeholder="Correo"
                    value={newPatient.email}
                    onChange={(e) => setNewPatient((n) => ({ ...n, email: e.target.value }))}
                    data-testid="new-patient-inline-email"
                  />
                </div>
                <input
                  list="appt-eps-list"
                  className="input-flat"
                  placeholder="EPS"
                  value={newPatient.eps}
                  onChange={(e) => setNewPatient((n) => ({ ...n, eps: e.target.value }))}
                  data-testid="new-patient-inline-eps"
                />
                <datalist id="appt-eps-list">
                  {EPS_LIST.map((e) => <option key={e} value={e} />)}
                </datalist>
                <button
                  type="button"
                  onClick={submitCreatePatient}
                  disabled={creating}
                  data-testid="new-patient-inline-save"
                  className="btn-primary w-full justify-center !h-9 disabled:opacity-60"
                >
                  <UserPlus className="w-4 h-4" />
                  {creating ? "Creando…" : "Guardar y seleccionar"}
                </button>
              </div>
            )}
            {form.patient_id && !createMode && (
              <div className="mt-2 text-xs text-primary font-mono" data-testid="appt-selected-patient">
                ✓ {form.patient_name}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-eyebrow block mb-2">Doctor</label>
              <select className="input-flat" value={form.doctor_id} onChange={(e)=>setForm(f=>({...f, doctor_id: e.target.value}))}>
                <option value="">—</option>
                {doctors.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Tratamiento</label>
              <select className="input-flat" value={form.treatment_id} onChange={(e)=>onTreatmentChange(e.target.value)}>
                <option value="">—</option>
                {treatments.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-eyebrow block mb-2">Inicio</label>
              <input type="datetime-local" className="input-flat" value={form.start} onChange={(e) => {
                const newStart = e.target.value;
                setForm((f) => {
                  // Preservar la duración previa entre start y end (o usar duración del tratamiento, o 30 min)
                  let durationMin = 30;
                  if (f.start && f.end) {
                    const prev = (new Date(f.end).getTime() - new Date(f.start).getTime()) / 60000;
                    if (prev > 0) durationMin = prev;
                  } else if (f.treatment_id) {
                    const t = treatments.find((x) => x.id === f.treatment_id);
                    if (t?.duration_min) durationMin = t.duration_min;
                  }
                  let newEnd = f.end;
                  if (newStart) {
                    newEnd = toLocalInput(new Date(new Date(newStart).getTime() + durationMin * 60000).toISOString());
                  }
                  return { ...f, start: newStart, end: newEnd };
                });
              }} />
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Fin</label>
              <input type="datetime-local" className="input-flat" value={form.end} onChange={(e)=>setForm(f=>({...f, end: e.target.value}))} />
            </div>
          </div>

          <div>
            <label className="label-eyebrow block mb-2">Estado</label>
            <select className="input-flat disabled:opacity-60 disabled:cursor-not-allowed" data-testid="appt-status-select" value={form.status} disabled={isLocked} onChange={(e)=>handleDropdownStatus(e.target.value)}>
              {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
            </select>
            {isLocked && (
              <p data-testid="appt-locked-notice" className="mt-2 text-xs text-muted-foreground flex items-center gap-1.5">
                <Lock className="w-3 h-3" /> Cita finalizada: el estado quedó bloqueado y no puede modificarse.
              </p>
            )}
            {isLocked && user?.role === "admin" && (
              <button
                type="button" onClick={() => setAnnulOpen(true)} data-testid="appt-annul-btn"
                className="mt-3 inline-flex items-center gap-2 h-9 px-3 rounded-sm border border-destructive/40 text-destructive text-xs font-bold uppercase tracking-widest hover:bg-destructive/10"
              >
                <Ban className="w-3.5 h-3.5" /> Anular cita finalizada
              </button>
            )}
          </div>

          <div>
            <label className="label-eyebrow block mb-2">Notas</label>
            <textarea rows={3} className="input-flat h-auto py-2" value={form.notes} onChange={(e)=>setForm(f=>({...f, notes: e.target.value}))} />
          </div>

          <div className="flex items-center gap-2 pt-2">
            <button type="submit" data-testid="appt-save" className="btn-primary flex-1 justify-center">
              {isNew ? "Crear cita" : "Guardar cambios"}
            </button>
            {!isNew && (
              <button type="button" onClick={onDelete} data-testid="appt-delete" className="h-10 w-10 rounded-sm border border-border text-destructive hover:bg-destructive/10 flex items-center justify-center" title="Eliminar">
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>

          {!isNew && !isLocked && (
            <div className="border-t border-border pt-3">
              <div className="label-eyebrow mb-2">Cambiar estado rápido</div>
              <div className="flex flex-wrap gap-2">
                {STATUSES.map((s) => (
                  <button
                    type="button" key={s}
                    onClick={() => handleQuickStatus(s)}
                    data-testid={`quick-status-${s}`}
                    className={`text-[10px] uppercase tracking-widest font-bold px-2.5 py-1.5 rounded-sm border ${form.status === s ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}
                  >
                    {STATUS_LABEL[s]}
                  </button>
                ))}
              </div>
            </div>
          )}

          {!isNew && (
            <FinalizeAppointmentDialog
              open={finalizeOpen}
              onClose={() => setFinalizeOpen(false)}
              appointment={appointment}
              doctor={doctors.find((d) => d.id === form.doctor_id)}
              doctorName={(doctors.find((d) => d.id === form.doctor_id) || {}).name}
              onFinalized={() => {
                setFinalizeOpen(false);
                setForm((f) => ({ ...f, status: "finalizada" }));
                onSaved && onSaved();
              }}
            />
          )}
          {!isNew && (
            <AnnulAppointmentDialog
              open={annulOpen}
              onOpenChange={setAnnulOpen}
              appointment={appointment}
              onAnnulled={() => onSaved && onSaved()}
            />
          )}
        </form>
      </SheetContent>
    </Sheet>
  );
}
